/**
 * The record's Tasks section (SA-461, sweep spec §7.4): what leaves the
 * process band. The open tasks as rows — title, `seat · due`, the kind when
 * it is not plain work (ruling R12), an Open button (the task page when the
 * viewer can open it, else the step's drawer — SA-480) — then the completed
 * tasks under an inner disclosure, fetched on its first open (the one
 * section whose data is not fetched on mount). The process read is the
 * band's (one React Query key), so nothing here is a second request.
 * SA-1034 PR 5 (spec §3.9): the viewer's own open task is the Current task block first in the list,
 * and the section opens by default while one exists.
 */
import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Badge, Button } from '@/components/ui'
import { useDefaultTimezone } from '@/hooks/useDefaultTimezone'
import { processErrorMessage, useRecordProcess, useRecordTasks } from '@/hooks/useProcess'
import { dueLine, kindWord, whenText } from '@/components/process/stepLiveText'
import { fromHere, taskLinkState, taskTargetOf } from '@/components/process/taskLink'
import type { TaskRead } from '@/types/process'
import { CURRENT_TASK, OVERDUE, currentTaskMeta } from './currentTaskText'
import { InnerDisclosure } from './InnerDisclosure'
import { ownedSections } from './laneDefaults'
import { firstOpenable, openTaskAction } from './openTaskAction'
import { useOpenPanel } from './panelLanding'
import { readSummary, readUnavailable } from './readState'
import { RecordSection } from './RecordSection'
import { tasksSummary } from './sectionSummaries'

const join = (parts: Array<string | null | undefined | false>): string => parts.filter(Boolean).join(' · ')

function Row({ task, meta, variant, onOpen }: { task: TaskRead; meta: string; variant: 'default' | 'outline'; onOpen: () => void }) {
  return (
    <li className="flex items-center gap-3 py-2 first:pt-0 last:pb-0">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-foreground">{task.step_number} {task.step_title}</p>
        {meta && <p className="text-xs text-muted-foreground">{meta}</p>}
      </div>
      <Button size="sm" variant={variant} aria-label={`Open ${task.step_title}`} onClick={onOpen}>Open</Button>
    </li>
  )
}

export function TasksSection({ orderId, noun, focused = false, onSelectStep }: {
  orderId: string; noun: string; focused?: boolean
  /** SA-480: the page's `selectStep` — a task the viewer cannot open opens its step drawer (replace, never push). */
  onSelectStep: (stepId: string) => void
}) {
  const record = useRecordProcess(orderId)
  const [wantDone, setWantDone] = useState(false)
  // `undefined` = nothing cached; `null` = no process (a loaded value). Read
  // through the data, never `isSuccess`, which is false after a failed refetch.
  const loaded = record.data !== undefined
  const data = record.data ?? null
  const done = useRecordTasks(wantDone && data ? orderId : undefined, 'done')
  const navigate = useNavigate()
  const location = useLocation()
  const open = (t: TaskRead) => {
    const target = taskTargetOf(t)
    if (target.kind === 'task') navigate(target.to, { state: taskLinkState(fromHere(location)) })
    else onSelectStep(target.stepId)
  }
  // SA-1034 PR 5 (spec §3.9): the viewer's first openable task is the block, the band's own rule.
  const current = firstOpenable(data)
  const zone = useDefaultTimezone()
  const openPanel = useOpenPanel()
  const goCurrent = () => {
    if (!current || !data) return
    const action = openTaskAction(current, data)
    if (action.kind === 'panel') openPanel(action.panel)
    else navigate(action.to, { state: taskLinkState(fromHere(location)) })
  }
  const others = data ? data.open_tasks.filter((t) => t.id !== current?.id) : []
  const summary = readSummary(record, (d) => tasksSummary(d, noun, new Date(), zone))
  return (
    <RecordSection id="tasks" tour="records.record.tasks" title="Tasks" summary={summary} defaultOpen={ownedSections(data).has('tasks')} focused={focused}>
      {record.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {readUnavailable(record) && <p className="text-sm text-destructive">{processErrorMessage(record.error)}</p>}
      {loaded && data === null && <p className="text-sm text-muted-foreground">No process runs on this {noun.toLowerCase()} yet.</p>}
      {data && (
        <div className="space-y-3">
          {current && (
            <section aria-label={CURRENT_TASK} data-testid="current-task" className="rounded-lg border border-primary/40 bg-primary/5 px-3 py-2.5">
              <p className="text-[11px] font-semibold uppercase tracking-[.08em] text-primary">{CURRENT_TASK}</p>
              <div className="mt-1 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-foreground">{current.step_number} {current.step_title}</p>
                  <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                    <span>{currentTaskMeta(current.seat_label, dueLine(current.due_at, new Date(), zone), current.kind)}</span>
                    {current.overdue && <Badge variant="destructive">{OVERDUE}</Badge>}
                  </p>
                </div>
                <Button size="sm" onClick={goCurrent}>{openTaskAction(current, data).label}</Button>
              </div>
            </section>
          )}
          {others.length > 0 ? (
            <ul className="divide-y divide-border">
              {others.map((t) => (
                <Row key={t.id} task={t} variant="default" onOpen={() => open(t)}
                  meta={join([t.seat_label, t.due_at && `due ${dueLine(t.due_at, new Date(), zone)}`, t.kind !== 'work' && t.kind])} />
              ))}
            </ul>
          ) : !current && (
            <p className="text-sm text-muted-foreground">
              {data.status === 'running' ? 'No task open; the step runs on its own.' : `None open. The ${noun.toLowerCase()} is closed.`}
            </p>
          )}
          <InnerDisclosure title="Completed tasks" onOpen={() => setWantDone(true)}>
            {done.isLoading && <p className="text-xs text-muted-foreground">Loading…</p>}
            {done.isError && <p className="text-xs text-destructive">{processErrorMessage(done.error)}</p>}
            {done.data && done.data.tasks.length === 0 && <p className="text-xs text-muted-foreground">Nothing has been completed yet.</p>}
            {done.data && done.data.tasks.length > 0 && (
              <ul className="divide-y divide-border">
                {done.data.tasks.map((t) => (
                  <Row key={t.id} task={t} variant="outline" onOpen={() => open(t)}
                    meta={join([t.result?.completed_by_name ?? (t.kind === 'work' || t.kind === 'attention' ? null : kindWord(t.kind)), whenText(t.completed_at), t.result_exit && `“${t.result_exit}”`])} />
                ))}
              </ul>
            )}
          </InnerDisclosure>
        </div>
      )}
    </RecordSection>
  )
}
